<script lang="ts">
export enum SELLER_TAB {
  MERCHANT_SHOP = 'merchant_shop',
  PIRATES_MARKET = 'pirates_market'
}
</script>

<template>
  <el-dialog
    v-model="showSellerModalInjected"
    append-to-body
    class="sellerModal"
    :class="{ 'ru-locale': $i18n.locale === 'ru', sellerModalMobile: $device.isMobile }"
    :center="true"
    destroy-on-close
    :fullscreen="$device.isMobile"
    @closed="handleSellerModalClosed"
    @opened="$emit('opened')"
  >
    <template #header>
      <UILinearTitle>
        <div class="sellerModalTitle">
          <span>{{ $t('seller') }}</span>
          <span class="sellerStatusOpen">{{ sellerModalStatus }}</span>
        </div>
      </UILinearTitle>

      <SellerModalInfoTooltip />
    </template>

    <template #default>
      <el-dialog
        v-model="showSellerModalConfirm"
        class="sellerModalConfirmBuy"
        :class="{ 'ru-locale': $i18n.locale === 'ru' }"
        :fullscreen="$device.isMobile"
        :title="$t('coreTokenPurchase')"
        append-to-body
        :center="true"
        destroy-on-close
        @closed="handleCloseModalConfirmBuy"
      >
        <SellerModalConfirmBuy
          v-if="selectedToken"
          :count="countProducts"
          :product="selectedToken"
          @click-buy="handleClickBuy"
          @click-max="handleClickMax"
        />
      </el-dialog>

      <el-dialog
        v-model="showInsufficientFundsDialog"
        :title="$t('buypackButtonInsufficientFunds')"
        :fullscreen="$device.isMobileOrTablet"
        class="insufficient-funds-modal"
        :class="{ 'ru-locale': $i18n.locale === 'ru' }"
        append-to-body
        destroy-on-close
        align-center
      >
        <InsufficientFundsModal :price="transactionPrice" @close="showInsufficientFundsDialog = false">
          <template #main-text>
            <span v-html="$t('tokenButtonPurchaseCosts')" />
          </template>
        </InsufficientFundsModal>
      </el-dialog>

      <el-dialog
        v-if="selectedToken"
        v-model="showSuccessDialog"
        :fullscreen="$device.isMobile"
        :title="$t('coreTransactionModalSuccessTitle')"
        append-to-body
        destroy-on-close
        class="transaction-modal-success"
        :class="{ 'ru-locale': $i18n.locale === 'ru' }"
        @closed="handleCloseSuccessDialog"
      >
        <TransactionModalSuccess
          :icon-src="getTokenIconSrc(selectedToken.tokenAddress, selectedToken.index ?? 0)"
          :price="transactionPrice"
          :count-token-purchased="countTokensPurchased"
          :token="tokenMetaData(blockchain.contracts, selectedToken.tokenAddress)"
          :link-to-transaction="linkToTransaction"
          @close="handleCloseSuccessDialog"
        />
      </el-dialog>

      <el-dialog
        v-if="selectedToken"
        v-model="showErrorDialog"
        class="transaction-modal-error"
        :class="{ 'ru-locale': $i18n.locale === 'ru' }"
        :fullscreen="$device.isMobile"
        :title="$t('coreTransactionModalErrorTitle')"
        destroy-on-close
        append-to-body
        @closed="handleCloseErrorDialog"
      >
        <TransactionModalError
          :icon-src="getTokenIconSrc(selectedToken.tokenAddress, selectedToken.index ?? 0)"
          :error-message="errorMessage"
          @close="handleCloseErrorDialog"
        />
      </el-dialog>

      <div v-if="isSellerOpen" class="top-toolbar">
        <div class="balance-wrapper">
          <UIBalance
            v-if="address"
            :key="balanceKey"
            :token-address="blockchain.contracts.usdt"
            :label="$t('coreBalance')"
          />
        </div>
        <div class="tabs-wrapper">
          <UITabs v-model="selectedTab" :tabs="tabs" size="sm" />
        </div>
      </div>

      <UIBlurWrapper>
        <div class="seller-modal-content">
          <div
            class="seller-container"
            :class="{
              'seller-container-closed': !isSellerOpen
            }"
          >
            <SellerScene />
            <UICard
              :class="{
                'card-sold-out': !isSellerOpen
              }"
            >
              <span v-if="isSellerOpen">{{ $t('sellerMessage') }}</span>
              <p v-else v-html="$t('sellerSoldOut')"></p>
            </UICard>
          </div>

          <!-- Merchant's Shop Tab - with WebSocket skeleton -->
          <el-skeleton
            v-if="selectedTab === SELLER_TAB.MERCHANT_SHOP"
            :loading="sellerStatusWS && sellerStatusWS === 'CONNECTING'"
            animated
            class="seller-lots"
          >
            <template #template>
              <el-skeleton-item v-for="index in 6" :key="index" class="skeleton-product-card" />
            </template>

            <template #default>
              <div v-if="isSellerOpen" class="seller-lots">
                <el-tooltip
                  v-for="item in sellerData"
                  :key="item.tokenAddress"
                  trigger="click"
                  popper-class="popper-default popper-seller"
                  placement="bottom"
                  :auto-close="1000"
                  :offset="-150"
                  :disabled="Boolean(item.amount) || !address"
                >
                  <template #content>
                    <span>{{ $t('sellerNoTokens') }}</span>
                  </template>

                  <UIProductCard
                    :icon-src="
                      tokens[tokenMetaData(blockchain.contracts, item.tokenAddress).name.contract as Tokens].toString()
                    "
                    :product-title="
                      $t(tokenMetaData(blockchain.contracts, item.tokenAddress).label as Tokens, { count: 0 })
                    "
                    :product-amount="item.amount"
                    :product-price="item.price"
                    @click="() => handleClick(item)"
                  />
                </el-tooltip>
              </div>

              <div v-else class="sold-out-container">
                <UICard>
                  <p v-html="$t('sellerSoldOut')"></p>
                </UICard>

                <div class="sold-out-img-container">
                  <img src="../../public/img/sellerModal/soldout.png" alt="sold-out-image" />
                </div>
              </div>
            </template>
          </el-skeleton>

          <!-- Pirate's Market Tab - completely independent, no skeleton wrapper -->
          <div v-else-if="selectedTab === SELLER_TAB.PIRATES_MARKET" class="seller-lots pirates-market-lots">
            <UIProductCard
              v-for="item in piratesMarketData"
              :key="`${item.tokenAddress}-${item.tokenIndex}`"
              :icon-src="getTokenIconSrc(item.tokenAddress, item.tokenIndex)"
              :product-title="getTokenLabel(item.tokenAddress, item.tokenIndex)"
              :product-price="formatPrice(item.price)"
              hide-available
              @click="() => handlePirateMarketClick(item)"
            />

            <!-- Show no data message only when not loading and no data -->
            <div v-if="!isPiratesMarketLoading && piratesMarketData.length === 0" class="no-data-message">
              <span>{{ $t('sellerNoTokens') }}</span>
            </div>
          </div>
        </div>
      </UIBlurWrapper>

      <div v-if="$device.isMobile && !isSellerOpen" class="button-wrapper">
        <button class="btn-primary" @click="$emit('closed')">
          <span>{{ $t('coreClose') }}</span>
        </button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { Tokens } from '~/utils/constants';
import tokens from 'public/img/tokens';
import type { SellerItem } from '~/types/seller';
import TransactionModalError from '~/components/TransactionModalError.vue';
import type { WebSocketStatus } from '@vueuse/core';
import UITabs from '~/shared/components/UITabs.vue';
import usePiratesMarket, { type PirateMarketItem } from '~/composables/usePiratesMarket';
import usePiratesMarketStatus from '~/composables/usePiratesMarketStatus';

const emit = defineEmits<{
  closed: [];
  opened: [];
}>();

const { t } = useI18n();
const store = useMainStore();
const { login } = useAuth();
const { blockchain } = useEnvs();
const { address } = useWeb3ModalAccount();

// Merchant shop (existing) buy token
const {
  buyToken,
  isButtonLoading: isMerchantButtonLoading,
  countTokensPurchased: merchantCountTokensPurchased,
  transactionPrice: merchantTransactionPrice,
  showInsufficientFundsDialog: merchantShowInsufficientFundsDialog,
  showErrorDialog: merchantShowErrorDialog,
  showSuccessDialog: merchantShowSuccessDialog,
  linkToTransaction: merchantLinkToTransaction,
  errorMessage: merchantErrorMessage
} = await useBuyToken();

// Pirate market buy token
const {
  piratesMarketData,
  isLoading: isPiratesMarketLoadingRaw,
  hasInitiallyLoaded: piratesMarketHasLoaded,
  buyFromPiratesMarket,
  isButtonLoading: isPiratesMarketButtonLoading,
  countTokensPurchased: piratesCountTokensPurchased,
  transactionPrice: piratesTransactionPrice,
  showInsufficientFundsDialog: piratesShowInsufficientFundsDialog,
  showErrorDialog: piratesShowErrorDialog,
  showSuccessDialog: piratesShowSuccessDialog,
  linkToTransaction: piratesLinkToTransaction,
  errorMessage: piratesErrorMessage
} = await usePiratesMarket();

// Only show loading before initial load completes
const isPiratesMarketLoading = computed(() => !piratesMarketHasLoaded.value && isPiratesMarketLoadingRaw.value);

const MIN_COUNT = 1;
const showSellerModalConfirm = ref(false);
const selectedToken = ref<SellerItem>();
const selectedPirateToken = ref<PirateMarketItem>();
const countProducts = ref(MIN_COUNT);
const balanceKey = ref(Date.now());
const isPirateMarketPurchase = ref(false);

// Computed properties that switch based on purchase type
const isButtonLoading = computed(() =>
  isPirateMarketPurchase.value ? isPiratesMarketButtonLoading.value : isMerchantButtonLoading.value
);
const countTokensPurchased = computed(() =>
  isPirateMarketPurchase.value ? piratesCountTokensPurchased.value : merchantCountTokensPurchased.value
);
const transactionPrice = computed(() =>
  isPirateMarketPurchase.value ? piratesTransactionPrice.value : merchantTransactionPrice.value
);
const showInsufficientFundsDialog = computed({
  get: () =>
    isPirateMarketPurchase.value ? piratesShowInsufficientFundsDialog.value : merchantShowInsufficientFundsDialog.value,
  set: (val) => {
    if (isPirateMarketPurchase.value) {
      piratesShowInsufficientFundsDialog.value = val;
    } else {
      merchantShowInsufficientFundsDialog.value = val;
    }
  }
});
const showErrorDialog = computed({
  get: () => (isPirateMarketPurchase.value ? piratesShowErrorDialog.value : merchantShowErrorDialog.value),
  set: (val) => {
    if (isPirateMarketPurchase.value) {
      piratesShowErrorDialog.value = val;
    } else {
      merchantShowErrorDialog.value = val;
    }
  }
});
const showSuccessDialog = computed({
  get: () => (isPirateMarketPurchase.value ? piratesShowSuccessDialog.value : merchantShowSuccessDialog.value),
  set: (val) => {
    if (isPirateMarketPurchase.value) {
      piratesShowSuccessDialog.value = val;
    } else {
      merchantShowSuccessDialog.value = val;
    }
  }
});
const linkToTransaction = computed(() =>
  isPirateMarketPurchase.value ? piratesLinkToTransaction.value : merchantLinkToTransaction.value
);
const errorMessage = computed(() =>
  isPirateMarketPurchase.value ? piratesErrorMessage.value : merchantErrorMessage.value
);

// Tabs
const selectedTab = ref<SELLER_TAB>(SELLER_TAB.MERCHANT_SHOP);
const tabs = computed(() => [
  { label: t('sellerMerchantShop'), value: SELLER_TAB.MERCHANT_SHOP },
  { label: t('sellerPiratesMarket'), value: SELLER_TAB.PIRATES_MARKET }
]);

// Helper functions for token display
const getTokenIconSrc = (tokenAddress: string, tokenIndex: number = 0): string => {
  const meta = tokenMetaData(blockchain.contracts, tokenAddress);
  if (!meta?.name?.contract) return '';

  const tokenImages = tokens[meta.name.contract as Tokens];

  // Handle NFT tokens (arrays of images indexed by tokenIndex)
  if (Array.isArray(tokenImages)) {
    return tokenImages[tokenIndex]?.toString() || tokenImages[0]?.toString() || '';
  }

  // Handle regular tokens (single image)
  return tokenImages?.toString() || '';
};

const getTokenLabel = (tokenAddress: string, tokenIndex: number = 0): string => {
  const meta = tokenMetaData(blockchain.contracts, tokenAddress);
  if (!meta?.label) return '';

  const label = t(meta.label as Tokens, { count: 0 });

  // For NFT tokens (arrays), append the index to differentiate
  const tokenImages = tokens[meta.name?.contract as Tokens];
  if (Array.isArray(tokenImages) && tokenImages.length > 1) {
    return `${label} #${tokenIndex + 1}`;
  }

  return label;
};

// Dynamic price formatter: shows minimum decimals needed to display significant digits
const formatPrice = (price: number): string => {
  if (price === 0) return '0';

  // If price >= 1, use 2 decimals max
  if (price >= 1) {
    return price.toFixed(2).replace(/\.?0+$/, '') || '0';
  }

  // For small prices, find how many decimals we need to show significant digits
  const priceStr = price.toString();

  // Handle scientific notation (e.g., 1e-7)
  if (priceStr.includes('e')) {
    const fixed = price.toFixed(10);
    // Remove trailing zeros but keep at least significant digits
    return fixed.replace(/0+$/, '').replace(/\.$/, '');
  }

  // Count leading zeros after decimal point
  const match = priceStr.match(/^0\.0*/);
  if (match) {
    const leadingZeros = match[0].length - 2; // subtract "0."
    // Show enough decimals to display 2 significant digits after zeros
    const decimals = Math.max(2, leadingZeros + 2);
    return price.toFixed(decimals).replace(/0+$/, '').replace(/\.$/, '');
  }

  return price.toFixed(2);
};

const showSellerModalInjected = inject<Ref<boolean>>('showSellerModal');

const sellerData = inject<Ref<SellerItem[]>>('sellerData');
const sellerStatusWS = inject<Ref<WebSocketStatus>>('sellerStatusWS');
const isSellerOpen = inject<Ref<boolean, boolean>>('sellerIsOpen');
const sellerModalStatusInjected = inject<Ref<string, string>>('sellerModalStatus');
const showCraftingModal = inject<Ref<boolean> | null>('showCraftingModal', null);

// Pirates market status with 1-hour timer
const { modalStatus: piratesMarketModalStatus } = usePiratesMarketStatus();

// Show different timer based on selected tab
const sellerModalStatus = computed(() => {
  if (selectedTab.value === SELLER_TAB.PIRATES_MARKET) {
    return piratesMarketModalStatus.value;
  }
  return sellerModalStatusInjected?.value || '';
});

const totalPrice = computed(() => {
  if (isPirateMarketPurchase.value && selectedPirateToken.value) {
    return parseFloat((selectedPirateToken.value.price * countProducts.value).toFixed(5));
  }
  if (!selectedToken.value) {
    return 0;
  }
  return parseFloat((selectedToken.value.price * countProducts.value).toFixed(5));
});

const handleClick = async (item: SellerItem) => {
  selectedToken.value = item;
  selectedPirateToken.value = undefined;
  isPirateMarketPurchase.value = false;

  if (!address.value) {
    await login();
    return;
  }

  if (!selectedToken.value?.amount) {
    return;
  }

  showSellerModalConfirm.value = true;
};

const handlePirateMarketClick = async (item: PirateMarketItem) => {
  // Convert to SellerItem for the confirm modal
  // Use a large number to indicate unlimited supply (will be detected by SellerModalConfirmBuy)
  const UNLIMITED_SUPPLY_MARKER = 999999999;
  selectedToken.value = {
    snapshotDate: new Date().toISOString(),
    tokenAddress: item.tokenAddress,
    amount: UNLIMITED_SUPPLY_MARKER,
    price: item.price,
    index: item.tokenIndex
  };
  selectedPirateToken.value = item;
  isPirateMarketPurchase.value = true;

  if (!address.value) {
    await login();
    return;
  }

  showSellerModalConfirm.value = true;
};

const handleClickBuy = async () => {
  if (isPirateMarketPurchase.value && selectedPirateToken.value) {
    await buyFromPiratesMarket(
      selectedPirateToken.value.tokenAddress,
      selectedPirateToken.value.tokenIndex,
      countProducts.value,
      selectedPirateToken.value.rawPrice
    );

    balanceKey.value = Date.now();
    showSellerModalConfirm.value = false;
    return;
  }

  if (!selectedToken.value?.tokenAddress || typeof selectedToken.value?.index !== 'number') {
    return;
  }

  await buyToken(selectedToken.value.index, countProducts.value, totalPrice.value, selectedToken.value.tokenAddress);

  balanceKey.value = Date.now();
  showSellerModalConfirm.value = false;
};

const handleClickMax = () => {
  if (isPirateMarketPurchase.value) {
    // For pirate market, there's no max limit from the contract
    // Set a reasonable default or let user enter any amount
    countProducts.value = 100;
    return;
  }

  if (!sellerData?.value) {
    return;
  }

  const token = sellerData.value?.find((item) => item.tokenAddress === selectedToken.value?.tokenAddress);

  if (!token) {
    return;
  }

  countProducts.value = token.amount;
};

const setCountProducts = (value: number) => {
  countProducts.value = value;
};

const handleCloseModalConfirmBuy = () => {
  countProducts.value = MIN_COUNT;
  // Don't reset purchase type flags here if success/error dialog is showing
  // They will be reset when those dialogs close
  if (!showSuccessDialog.value && !showErrorDialog.value) {
    isPirateMarketPurchase.value = false;
    selectedPirateToken.value = undefined;
  }
};

const handleCloseSuccessDialog = () => {
  showSuccessDialog.value = false;
  // Reset flags after success dialog closes
  isPirateMarketPurchase.value = false;
  selectedPirateToken.value = undefined;
};

const handleCloseErrorDialog = () => {
  showErrorDialog.value = false;
  // Reset flags after error dialog closes
  isPirateMarketPurchase.value = false;
  selectedPirateToken.value = undefined;
};

const handleSellerModalClosed = () => {
  emit('closed');
  if (!showCraftingModal?.value) {
    store.openedBuilding = null;
  }
};

provide('countProducts', countProducts);
provide('setCountProducts', setCountProducts);
provide('totalPrice', totalPrice);
provide('buyButtonLoading', isButtonLoading);
</script>
<style scoped lang="scss">
.seller-modal-content {
  display: flex;
  gap: 15px;
  margin: 16px 18px 16px 25px;
  justify-content: center;

  @media screen and (max-width: 479px) {
    margin: 16px 5px;
  }

  .seller-container {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 210px;
    min-height: 380px;

    @media screen and (max-width: 900px) {
      display: none;
    }

    .card-default {
      position: absolute;
      bottom: 0;
      font-family: Eczar;
      font-weight: 500;
      font-size: 14px;
      line-height: 1.2;
      text-align: center;
      padding: 16px;
      color: var(--white-70);
    }

    .card-sold-out {
      width: 270px;
    }
  }

  .seller-container-closed {
    max-width: 250px;
  }

  .seller-lots {
    display: grid;
    justify-content: center;
    grid-column-gap: 16px;
    grid-row-gap: 16px;
    grid-template-columns: repeat(2, minmax(180px, 180px));
    height: 476px;
    overflow-y: scroll;
    overflow-x: hidden;
    scroll-behavior: smooth;
    scrollbar-width: thin;
    padding-right: 10px;

    /* Custom scrollbar styling to match Figma */
    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #354750;
      border-radius: 5px;
    }

    @media screen and (max-width: 340px) {
      grid-template-columns: repeat(1, minmax(180px, 180px));
    }

    @media screen and (min-width: 767px) {
      grid-template-columns: repeat(3, minmax(180px, 180px));
    }

    .no-data-message {
      grid-column: 1 / -1;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 40px;
      font-family: Eczar;
      font-weight: 500;
      font-size: 18px;
      color: var(--white-70);
      text-align: center;
    }
  }

  .blur-wrapper {
    padding: 20px 10px;
  }

  .sold-out-container {
    display: flex;
    overflow: hidden;
    position: relative;

    .sold-out-img-container {
      display: flex;
      justify-content: center;
      min-width: 580px;

      img {
        width: 370px;
        height: 380px;
      }
    }

    .card-default {
      display: none;
      width: 340px;
      padding: 16px;
      text-align: center;

      font-family: Eczar;
      font-weight: 500;
      font-size: 18px;
      line-height: 1.2;
    }

    @media screen and (max-width: 900px) {
      .card-default {
        display: block;
        position: absolute;
        top: 100px;
        left: 50%;
        transform: translateX(-50%);
      }

      .sold-out-img-container {
        min-width: unset;
      }
    }
  }
}

.top-toolbar {
  display: flex;
  min-height: 50px;
  position: relative;
  margin: 0 0px 8px 0;
  gap: 16px;
  align-items: center;
  justify-content: space-between;

  @media screen and (max-width: 767px) {
    flex-direction: column;
    justify-content: center;
    gap: 12px;
    margin: 0 5px 8px 5px;
  }

  .balance-wrapper {
    flex-shrink: 0;

    @media screen and (max-width: 767px) {
      width: 100%;
    }
  }

  .tabs-wrapper {
    flex-shrink: 0;

    @media screen and (max-width: 767px) {
      width: 100%;

      :deep(.ui-tabs) {
        width: 100%;
        gap: 8px;
      }

      :deep(.ui-tab) {
        flex: 1;
        padding: 14px 12px;
        font-size: 16px;
        line-height: 1.2;
      }
    }
  }
}

.button-wrapper {
  display: block;
  position: absolute;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  z-index: 2;

  .btn-primary {
    width: 275px;
  }
}

.skeleton-product-card {
  width: 180px;
  height: 219px;
  min-width: 180px;
  max-height: 219px;
}

.el-dialog.sellerModalMobile {
  overflow: hidden;

  .seller-lots {
    grid-column-gap: 7px;
    grid-row-gap: 7px;
    padding-right: 5px;
    padding-left: 10px;
    height: 75dvh;
  }

  .seller-container {
    video {
      position: absolute;
      bottom: 80px;
    }
  }

  .sold-out-container {
    height: 75vh;
    display: flex;
    align-items: flex-start;
    justify-content: center;

    .card-default {
      width: 320px;
    }
  }

  .button-wrapper {
    button {
      height: 56px;
      min-width: 275px;
      display: none;

      @media screen and (max-width: 900px) {
        display: block;
      }
    }
  }
}
</style>

<style lang="scss">
.el-popper.popper-default.is-dark.popper-seller {
  max-width: 220px;
  font-weight: 700;
  font-size: 24px;
  line-height: 1.1;
  text-align: center;
}

.el-dialog.sellerModal {
  margin: 0;
  position: fixed;
  width: 100%;
  min-width: 350px;
  max-width: 500px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-image: url('~/public/img/sellerModal/seller-modal-bg.jpg');
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  padding: 8px 16px 16px;

  @media screen and (max-width: 479px) {
    padding-left: 5px;
    padding-right: 5px;
  }

  @media screen and (min-width: 767px) {
    max-width: 900px;
    min-height: 600px;
  }

  .el-dialog__body {
    min-width: 100%;
    margin: 0;
    padding-top: 16px;
    padding-bottom: 0;
  }

  &:after {
    content: '';
    display: block;
    z-index: -1;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(8, 27, 37, 0.4);
  }

  .card-default {
    p {
      color: var(--main-text-color);
    }

    p > span {
      color: var(--blue_highlight);
    }
  }

  .sellerModalTitle {
    display: flex;
    flex-direction: column;
    gap: 8px;
    white-space: nowrap;

    .sellerStatusOpen {
      font-family: Eczar;
      font-weight: 500;
      font-size: 20px;
      line-height: 1.1;
      color: var(--blue_highlight);
      min-height: 22px;
    }
  }

  .top-toolbar .balance .price-container img {
    width: 21px;
    height: 17px;
  }

  .blur-wrapper {
    overflow: hidden;
  }

  @media screen and (min-width: 900px) {
    .blur-wrapper {
      background: #081b25;
    }
  }

  .el-overlay-dialog {
    overflow: hidden;
  }
}

.el-dialog.sellerModalMobile {
  max-width: unset;
  min-width: unset;
  padding: 0 4px;
  overflow: hidden;
  height: 100dvh;

  @media screen and (max-width: 375px) {
    padding-left: 2px;
    padding-right: 2px;
  }

  .el-dialog__headerbtn {
    top: 15px;
  }

  .el-dialog__header.show-close {
    padding-top: 0;
  }

  .el-dialog__body {
    padding-top: 8px;
  }

  .blur-wrapper {
    background: transparent;
    backdrop-filter: unset;
    border: unset;
    max-height: unset;
  }

  @media screen and (min-width: 900px) {
    .blur-wrapper {
      background: #081b25;
    }

    .blur-wrapper.wrapperMacOs {
      background: #021d2b;
    }
  }
}
</style>
